import type { CandidateSummary } from "@/api/preview";
import type { FinalSummaryContent } from "@/api/previewFinalSummary";
import type { LabTurn } from "@/lib/labModel";
import type { LabRunModel } from "@/lib/labRunModel";
import {
  readContentSlot,
  type ContentSlot,
  type TraceEvent,
} from "@/lib/previewDecisions";

/**
 * Reliv Labs export: ONE JSON file for one run, built from what the Lab has
 * already read. Pure: no React, no network, no clock (the caller passes `now`).
 *
 * Patient and Liv text is only what the body-bearing event read returned (or
 * what the compact page carried inline); exact prompt bodies are included only
 * when the caller hands them in. Nothing is redacted or reworded here: a slot
 * the producer withheld is exported as its state and omission reason, never as
 * a guess.
 */

export const LAB_EXPORT_SCHEMA = "reliv-labs-export.v2";

/** One conversation side as exported. `text` is null unless the body was read. */
export type LabExportContent = {
  state: string;
  text: string | null;
  contentHash: string | null;
  omittedReason: string | null;
};

export type LabExportTurnText = {
  patient: LabExportContent | null;
  liv: LabExportContent | null;
};

export type LabExportCompactEvent = {
  eventRef: string;
  sequence: number;
  turnNumber: number | null;
  kind: string;
  label: string;
  status: string;
  occurredAt: string | null;
};

export type LabExportInput = {
  now: Date;
  project: { id: string; name: string | null; tenant: string | null };
  model: LabRunModel;
  /** The candidate the run was dispatched on, when it could be resolved; null otherwise. */
  candidate: CandidateSummary | null;
  /** Patient/Liv text per turn number (from the event-detail read). */
  texts: ReadonlyMap<number, LabExportTurnText>;
  /** Full event (bodies inline) per prompt-call event ref; null/absent = prompts not included. */
  prompts?: ReadonlyMap<string, unknown> | null;
  /** The tester's bug note; blank means the file carries no `report`. */
  note?: string;
  /** The run's final summary when one exists; null or absent means the file carries none. */
  finalSummary?: FinalSummaryContent | null;
};

function slotOut(slot: ContentSlot | null): LabExportContent | null {
  return slot === null
    ? null
    : {
        state: slot.state,
        text: slot.inline,
        contentHash: slot.contentHash,
        omittedReason: slot.omittedReason,
      };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The turn's text as the compact page already carries it: a slot that is not a
 * `reference` needs no further read. Null when a body read is still needed.
 */
export function inlineTurnText(turn: LabTurn): LabExportTurnText | null {
  const detail = turn.turnEvent?.detail;
  if (detail === undefined) {
    return null;
  }
  const slots = [detail.userInput, detail.livResponse];
  if (slots.some((slot) => slot !== null && slot.state === "reference")) {
    return null;
  }
  return {
    patient: slotOut(detail.userInput),
    liv: slotOut(detail.livResponse),
  };
}

/** The turn's text out of a body-bearing event-detail response (`{ event: { detail } }`). */
export function turnTextFromDetail(response: unknown): LabExportTurnText {
  const event = isRecord(response) ? response["event"] : undefined;
  const detail = isRecord(event) ? event["detail"] : undefined;
  if (!isRecord(detail)) {
    return { patient: null, liv: null };
  }
  return {
    patient: slotOut(readContentSlot(detail["userInput"])),
    liv: slotOut(readContentSlot(detail["livResponse"])),
  };
}

export const TRANSCRIPT_TEXT_MISSING = "[text not available]";

/**
 * The conversation as plain text: a `Patient:` line and a `Liv:` line per turn,
 * in turn order, a blank line between lines. A side whose text was withheld or
 * could not be read is marked, never left out.
 */
export function buildTranscript(
  turns: readonly Pick<LabTurn, "turnNumber">[],
  texts: ReadonlyMap<number, LabExportTurnText>,
): string {
  const lines: string[] = [];
  for (const turn of turns) {
    const text = texts.get(turn.turnNumber);
    lines.push(
      `Patient: ${text?.patient?.text ?? TRANSCRIPT_TEXT_MISSING}`,
      `Liv: ${text?.liv?.text ?? TRANSCRIPT_TEXT_MISSING}`,
    );
  }
  return lines.join("\n\n");
}

export function compactEvent(
  event: TraceEvent,
  turnNumber: number | null,
): LabExportCompactEvent {
  return {
    eventRef: event.eventRef,
    sequence: event.sequence,
    turnNumber,
    kind: event.kind,
    label: event.label,
    status: event.status,
    occurredAt: event.occurredAt,
  };
}

/** The earliest event time, which is the nearest thing to a run start the Lab reads. */
export function firstEventTime(model: LabRunModel): string | null {
  const times = model.lab.allEvents
    .map((event) => event.occurredAt)
    .filter(
      (time): time is string =>
        time !== null && !Number.isNaN(Date.parse(time)),
    )
    .sort((a, b) => Date.parse(a) - Date.parse(b));
  return times[0] ?? null;
}

/** The candidate whose content hash the run's identity names (newest first when several share one). */
export function candidateForRun(
  model: Pick<LabRunModel, "overview">,
  candidates: readonly CandidateSummary[],
): CandidateSummary | null {
  const hash =
    model.overview.identity.find((row) => row.axis === "candidateContentHash")
      ?.value ?? null;
  if (hash === null) {
    return null;
  }
  const matches = candidates.filter((c) => c.contentHash === hash);
  return (
    [...matches].sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
    )[0] ?? null
  );
}

function slug(text: string): string {
  const s = text
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return s === "" ? "project" : s.slice(0, 40);
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** reliv-labs_<project-name-slug>_<runref>_<YYYYMMDD-HHMM>.json (local time). */
export function labExportFilename(
  projectName: string | null,
  runRef: string,
  now: Date,
): string {
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const safeRef = runRef.replace(/[^A-Za-z0-9_-]+/g, "");
  return `reliv-labs_${slug(projectName ?? "")}_${safeRef}_${stamp}.json`;
}

export function buildLabExport(input: LabExportInput) {
  const { model, project, candidate, texts, prompts, now, note, finalSummary } =
    input;
  const { overview, sim, lab } = model;
  const turnByRef = new Map(
    model.raw.map((row) => [row.eventRef, row.turnNumber]),
  );
  const identity = Object.fromEntries(
    overview.identity.map((row) => [row.axis, row.value]),
  );

  return {
    schema: LAB_EXPORT_SCHEMA,
    exportedAt: now.toISOString(),
    ...(note !== undefined && note.trim() !== ""
      ? { report: { note, writtenAt: now.toISOString() } }
      : {}),
    project: { id: project.id, name: project.name, tenant: project.tenant },
    run: {
      ref: model.previewRunRef,
      status: sim?.status ?? overview.stateLabel,
      persona: sim?.personaId ?? null,
      language: sim?.language ?? null,
      maxTurns: sim?.maxTurns ?? null,
      endReason: sim?.endReason ?? null,
      startedAt: firstEventTime(model),
      simStats: sim?.simStats ?? [],
    },
    candidate:
      candidate === null
        ? null
        : {
            candidateId: candidate.candidateId,
            contentHash: candidate.contentHash,
            basePackSha: candidate.basePackSha,
            basePackRevision: candidate.basePackRevision ?? null,
            baseContentSha256: candidate.baseContentSha256 ?? null,
            revision: candidate.revision?.number ?? null,
            createdAt: candidate.createdAt,
            expiresAt: candidate.expiresAt,
          },
    identity,
    completeness: {
      captureState: overview.captureState,
      eventCount: overview.eventCount,
      lastSequence: overview.lastSequence,
      completeThroughSequence: overview.completeThroughSequence,
      sizeCapExceeded: overview.sizeCapExceeded,
      missingRanges: overview.missingRanges.map((r) => ({
        from: r.from,
        to: r.to,
        reason: r.reason,
      })),
      omissions: overview.omissions.map((o) => ({
        eventRef: o.eventRef,
        field: o.field,
        reason: o.reason,
      })),
      unreadableEvents: lab.unreadable,
    },
    turns: lab.turns.map((turn) => {
      const text = texts.get(turn.turnNumber) ?? null;
      return {
        turnNumber: turn.turnNumber,
        agent: turn.agent,
        track: turn.track,
        patient: text?.patient ?? null,
        liv: text?.liv ?? null,
        badges: turn.badges.map((b) => ({
          kind: b.kind,
          count: b.count,
          attention: b.attention,
        })),
        events: turn.events.map((e) => compactEvent(e, turn.turnNumber)),
      };
    }),
    allEvents: lab.allEvents.map((e) =>
      compactEvent(e, turnByRef.get(e.eventRef) ?? null),
    ),
    promptCalls: model.prompts.map((row) => ({
      eventRef: row.eventRef,
      turnNumber: row.turnNumber,
      callType: row.callType,
      model: row.model,
      latencyMs: row.latencyMs,
      ...(prompts ? { event: prompts.get(row.eventRef) ?? null } : {}),
    })),
    ...(finalSummary ? { finalSummary } : {}),
  };
}

export type LabExport = ReturnType<typeof buildLabExport>;

/**
 * Runs `task` over `items` with at most `limit` in flight, reporting how many
 * have finished. Results keep the input order; a task's rejection is caught and
 * returned as `{ ok: false }` so one unreadable turn never loses the rest.
 */
export async function mapLimited<T, R>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<R>,
  onProgress?: (done: number, total: number) => void,
): Promise<({ ok: true; value: R } | { ok: false })[]> {
  const out: ({ ok: true; value: R } | { ok: false })[] = new Array(
    items.length,
  );
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      try {
        out[index] = { ok: true, value: await task(items[index]!) };
      } catch {
        out[index] = { ok: false };
      }
      done += 1;
      onProgress?.(done, items.length);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(Math.max(limit, 1), items.length) }, worker),
  );
  return out;
}
